import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Apple,
  ArrowRight,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  GitBranch,
  Github,
  Laptop,
  Loader2,
  MonitorDown,
  ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { listPublicReleases, type Release } from "@/lib/console-api";
import { useAuthStore } from "@/stores/auth-store";

const linkBtn = cn(
  "inline-flex h-11 items-center justify-center gap-2 rounded-md border border-input bg-background px-6 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50",
);

const linkBtnPrimary = cn(
  "inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50",
);

/** 앱이 실제로 요구하는 것 — 데스크톱 온보딩 체크리스트(GitHub 토큰 · AI 연결)와 맞춘다. */
const REQUIREMENTS = [
  {
    icon: Laptop,
    title: "macOS 11 이상 또는 Windows 10 이상 (64비트)",
    body: "macOS는 Apple Silicon과 Intel 모두 지원합니다.",
  },
  {
    icon: GitBranch,
    title: "Git",
    body: "작업할 저장소를 내 컴퓨터에 받아 둔 상태여야 합니다.",
  },
  {
    icon: Github,
    title: "GitHub 계정과 개인 액세스 토큰",
    body: "브랜치를 올리고 PR을 만드는 데 씁니다. 토큰은 내 컴퓨터의 보안 저장소에만 보관됩니다.",
  },
  {
    icon: Bot,
    title: "AI 하나: Claude Code, OpenAI 키, Gemini 키 중 선택",
    body: "Claude Code는 설치 후 로그인만 하면 되고, AI 사용료는 각 서비스에 직접 냅니다. Devit은 AI 요금을 받지 않습니다.",
  },
];

const NEXT_STEPS = [
  "앱을 설치하고 Devit 계정으로 로그인",
  "설정에서 GitHub 토큰과 사용할 AI를 연결",
  "작업할 프로젝트 폴더를 연결하고, 만들 것을 한 줄로 적어 보세요",
];

export function DownloadPage(): JSX.Element {
  const status = useAuthStore((s) => s.status);
  const account = useAuthStore((s) => s.account);
  const isAuthenticated = status === "authenticated";
  const [showOlder, setShowOlder] = useState(false);

  const releasesQuery = useQuery({
    queryKey: ["releases-public"],
    queryFn: listPublicReleases,
  });

  const releases = releasesQuery.data ?? [];
  const latest = releases[0];
  const older = releases.slice(1);

  return (
    <main className="container py-16">
      <div className="mx-auto max-w-2xl text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          {isAuthenticated ? (
            <CheckCircle2 className="h-6 w-6" aria-hidden />
          ) : (
            <MonitorDown className="h-6 w-6" aria-hidden />
          )}
        </div>
        <h1 className="mt-6 text-3xl font-bold tracking-tight">
          {!isAuthenticated
            ? "Devit 데스크톱 앱"
            : account?.name
              ? `${account.name}님, 반가워요`
              : "반가워요"}
        </h1>
        <p className="mt-3 text-muted-foreground">
          AI 실행과 git 작업은 이 앱이 내 컴퓨터에서 처리합니다. 설치 전에 아래 준비물을 확인해
          주세요.
        </p>

        {/* 최신 릴리스 */}
        <div className="mt-10">
          {releasesQuery.isLoading && (
            <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> 다운로드 정보를 불러오는 중…
            </p>
          )}

          {!releasesQuery.isLoading && !latest && (
            <div className="rounded-lg border border-dashed border-border p-8 text-left text-sm text-muted-foreground">
              <p>아직 공개된 빌드가 없습니다.</p>
              <p className="mt-2">
                관리자가 첫 릴리스를 게시하면 이 자리에 다운로드 버튼이 나타납니다.
              </p>
            </div>
          )}

          {latest && <LatestRelease release={latest} />}
        </div>

        <Requirements />

        {latest && <InstallHelp />}

        {/* 이전 버전 */}
        {older.length > 0 && (
          <div className="mt-8">
            <button
              type="button"
              onClick={() => setShowOlder((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {showOlder ? (
                <>
                  <ChevronUp className="h-3 w-3" /> 이전 버전 숨기기
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" /> 이전 버전 보기 ({older.length})
                </>
              )}
            </button>
            {showOlder && (
              <ul className="mt-4 space-y-3 text-left">
                {older.map((r) => (
                  <OlderReleaseRow key={r.id} release={r} />
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-12 rounded-lg border border-border bg-card p-6 text-left">
          <h2 className="text-lg font-semibold">다음에 할 일</h2>
          <ol className="mt-4 space-y-3 text-sm">
            {NEXT_STEPS.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          {!isAuthenticated && (
            <div className="mt-6 flex flex-col gap-2 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">앱에서 쓸 계정이 아직 없다면</p>
              <Link
                to="/signup"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                무료로 가입하기 <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function Requirements(): JSX.Element {
  return (
    <div className="mt-6 rounded-lg border border-border bg-card p-6 text-left">
      <h2 className="text-lg font-semibold">시작 전에 필요한 것</h2>
      <ul className="mt-4 space-y-4">
        {REQUIREMENTS.map((r) => (
          <li key={r.title} className="flex gap-3">
            <r.icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
            <div>
              <p className="text-sm font-medium">{r.title}</p>
              <p className="mt-0.5 text-sm text-muted-foreground">{r.body}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LatestRelease({ release }: { release: Release }): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-6 text-left">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-xs text-muted-foreground">최신 버전</p>
          <p className="mt-1 font-mono text-2xl font-bold tracking-tight">
            v{release.version}
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          {new Date(release.publishedAt).toLocaleDateString()}
        </p>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {release.macArm64Url && (
          <a
            href={release.macArm64Url}
            className={linkBtnPrimary}
            aria-label="macOS Apple Silicon 다운로드"
          >
            <Apple className="h-5 w-5" />
            macOS Apple Silicon
          </a>
        )}
        {release.macX64Url && (
          <a
            href={release.macX64Url}
            className={linkBtn}
            aria-label="macOS Intel 다운로드"
          >
            <Apple className="h-5 w-5" />
            macOS Intel
          </a>
        )}
        {release.windowsUrl && (
          <a
            href={release.windowsUrl}
            className={cn(linkBtn, "sm:col-span-2")}
            aria-label="Windows 다운로드"
          >
            <MonitorDown className="h-5 w-5" />
            Windows
          </a>
        )}
      </div>

      {release.notes && (
        <details className="mt-5">
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
            릴리스 노트 보기
          </summary>
          <pre className="mt-2 whitespace-pre-wrap rounded bg-muted/50 p-3 text-xs">
            {release.notes}
          </pre>
        </details>
      )}

    </div>
  );
}

function InstallHelp(): JSX.Element {
  const fixCommand = "xattr -cr /Applications/devit.app";
  const [copied, setCopied] = useState(false);

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(fixCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 클립보드 권한 거부 — 무시 (사용자가 직접 선택해서 복사하면 됨)
    }
  };

  return (
    <div className="mt-6 rounded-lg border border-orange-500/30 bg-orange-500/5 p-5 text-left text-sm">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" aria-hidden />
        <div className="space-y-3">
          <div>
            <p className="font-medium text-foreground">
              "손상되어 열 수 없습니다" / "확인되지 않은 개발자" 경고가 뜬다면
            </p>
            <p className="mt-1 text-muted-foreground">
              devit 은 Apple 개발자 인증을 받지 않은 상태라 macOS 가 차단합니다.
              다음 한 줄로 해제할 수 있어요 (앱이 망가진 게 아닙니다).
            </p>
          </div>

          {/* macOS — 손상 메시지 */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              macOS — "손상" 메시지가 뜨는 경우
            </p>
            <ol className="mt-2 ml-4 list-decimal space-y-1.5 text-muted-foreground">
              <li>다운로드한 dmg 를 열어 <strong>devit</strong> 을 <strong>Applications</strong> 으로 드래그</li>
              <li>
                <span>터미널 (Spotlight 에서 "터미널" 검색) 을 열고 아래 명령 실행:</span>
                <div className="mt-1.5 flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2">
                  <code className="flex-1 font-mono text-xs text-foreground">
                    {fixCommand}
                  </code>
                  <button
                    type="button"
                    onClick={onCopy}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
                    aria-label="명령 복사"
                  >
                    <Copy className="h-3 w-3" />
                    {copied ? "복사됨" : "복사"}
                  </button>
                </div>
              </li>
              <li>다시 devit 더블클릭 → 정상 실행</li>
            </ol>
          </div>

          {/* macOS — 확인되지 않은 개발자 */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              macOS — "확인되지 않은 개발자" 만 뜨는 경우
            </p>
            <p className="mt-1.5 text-muted-foreground">
              Applications 폴더에서 <strong>devit 우클릭 → 열기</strong> →
              다이얼로그에서 다시 "열기". 첫 실행만 이렇게 하면 그 이후엔 그냥 실행됩니다.
            </p>
          </div>

          {/* Windows */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Windows — SmartScreen 경고
            </p>
            <p className="mt-1.5 text-muted-foreground">
              "Windows 의 PC 보호" 화면이 뜨면 <strong>"추가 정보" → "실행"</strong> 을 클릭.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function OlderReleaseRow({ release }: { release: Release }): JSX.Element {
  return (
    <li className="rounded-md border border-border bg-card p-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="font-mono font-medium">v{release.version}</span>
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {release.channel}
          </span>
          <span className="text-xs text-muted-foreground">
            {new Date(release.publishedAt).toLocaleDateString()}
          </span>
        </div>
        <div className="flex gap-2">
          {release.macArm64Url && (
            <a
              href={release.macArm64Url}
              className="text-xs underline-offset-4 hover:underline"
            >
              mac arm64
            </a>
          )}
          {release.macX64Url && (
            <a
              href={release.macX64Url}
              className="text-xs underline-offset-4 hover:underline"
            >
              mac x64
            </a>
          )}
          {release.windowsUrl && (
            <a
              href={release.windowsUrl}
              className="text-xs underline-offset-4 hover:underline"
            >
              windows
            </a>
          )}
        </div>
      </div>
    </li>
  );
}
